import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutPage = () => {
  const { t } = useTranslation();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const milestonesData = t('pages.about.milestones', { returnObjects: true });

  return (
    <>
      <Header />
      <div className="min-h-screen bg-light-bg">
        {/* Hero */}
        <section className="py-12 md:py-20 px-6 md:px-12 bg-gradient-to-b from-light-bg to-light-card">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <motion.h1
              variants={itemVariants}
              className="font-filson font-bold text-5xl md:text-6xl text-gray-900 mb-6"
            >
              {t('pages.about.title')}
            </motion.h1>
            <motion.p
              variants={itemVariants}
              className="font-filson font-medium text-xl text-gray-600 leading-relaxed"
            >
              {t('pages.about.subtitle')}
            </motion.p>
          </motion.div>
        </section>

        {/* Mission & Vision */}
        <section className="py-12 md:py-20 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="font-filson font-bold text-3xl text-gray-900 mb-4">
                  {t('pages.about.mission')}
                </h2>
                <p className="font-filson font-medium text-gray-600 leading-relaxed mb-6">
                  {t('pages.about.missionText')}
                </p>
                <p className="font-filson font-medium text-gray-500 leading-relaxed">
                  {t('pages.about.missionDesc')}
                </p>
              </motion.div>

              {/* Vision */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="font-filson font-bold text-3xl text-gray-900 mb-4">
                  {t('pages.about.vision')}
                </h2>
                <p className="font-filson font-medium text-gray-600 leading-relaxed mb-6">
                  {t('pages.about.visionText')}
                </p>
                <p className="font-filson font-medium text-gray-500 leading-relaxed">
                  {t('pages.about.visionDesc')}
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-12 md:py-20 px-6 md:px-12 bg-light-card">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="font-filson font-bold text-4xl text-gray-900 mb-12 text-center"
            >
              {t('pages.about.journey')}
            </motion.h2>

            <div className="space-y-8">
              {milestonesData.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="card"
                  style={{
                    borderRadius: '12px',
                    border: '1px solid #E7EAE5',
                    padding: '24px',
                    backgroundColor: 'white',
                  }}
                >
                  <div className="flex gap-6">
                    <div
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '12px',
                        backgroundColor: '#CB4B4B',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <span className="font-filson font-bold text-2xl text-white">
                        {milestone.year}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-filson font-bold text-xl text-gray-900 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="font-filson font-medium text-gray-600">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default AboutPage;
